import {
  ContractSwitchType,
  Cycle,
  LineItem,
} from '@wix/ambassador-premium-store/types';
import {
  CycleSelector,
  OrderPageCreationFunction,
  OrderPageDataResponse,
} from '@wix/checkout-components';
import { useCheckoutStore, CheckoutActions } from '../../../../providers';
import {
  convertCycleSelectorToPaymentCycle,
  convertPaymentCycleToCycle,
  PaymentCycle,
} from '../../../../utils';
import { CheckoutUrls } from '../../../../Api/checkout';
import { ApiManager } from '../../../../Api';
import { Product } from '../../../../types';

const CART_ITEMS_AMOUNT = 1;
const REJECT_REASON =
  'useOrderPageCreation Must be used with at least one argument';

interface OrderPageCreationBaseOptions {
  siteGuid: string;
  currentProduct: Product;
  currentCycle: PaymentCycle;
  isDowngradePlanOfDeclineChargeService?: boolean;
}

interface DynamicOfferingRequestOptions {
  isUpgrade: boolean;
  userPackageGuid?: string;
}

interface ClassicRequestOptions {
  couponServiceFallback?: string | null;
  couponCode?: string;
  oldSiteGuid?: string;
  existingUserPackageGuid?: string;
}

export interface UseOrderPageCreationOptions
  extends OrderPageCreationBaseOptions {
  dynamicOfferingRequestParams?: DynamicOfferingRequestOptions;
  classicRequestParams?: ClassicRequestOptions;
}

async function fetchDynamicOfferingOrderPageData({
  siteGuid,
  currentProduct,
  currentCycle,
  isDowngradePlanOfDeclineChargeService,
  isUpgrade,
  userPackageGuid,
  alternativeCycle,
}: DynamicOfferingRequestOptions &
  OrderPageCreationBaseOptions & {
    alternativeCycle?: Cycle;
  }): Promise<OrderPageDataResponse> {
  const cartItem: LineItem = {
    productId: currentProduct.guid ?? currentProduct.id.toString(),
    cycle: alternativeCycle ?? convertPaymentCycleToCycle(currentCycle),
    quantity: CART_ITEMS_AMOUNT,
    additionalData: {},
  };
  if (isUpgrade) {
    cartItem.switchContract = {
      contractSwitchType: isDowngradePlanOfDeclineChargeService
        ? ContractSwitchType.DOWNGRADE_RENEWAL
        : ContractSwitchType.CREDIT_UNUSED_PERIOD,
      existingSubscriptionId: userPackageGuid,
      msid: siteGuid,
    };
  } else {
    cartItem.newSubscription = { msid: siteGuid };
  }

  const cartItems = [cartItem];
  return ApiManager.post(CheckoutUrls.DYNAMIC_OFFERING_ORDER_PAGE_DATA, {
    cartItems,
  });
}

async function fetchClassicOrderPageData({
  siteGuid,
  currentProduct,
  currentCycle,
  isDowngradePlanOfDeclineChargeService,
  couponCode,
  oldSiteGuid,
  existingUserPackageGuid,
  couponServiceFallback,
  alternativeCycle,
}: ClassicRequestOptions &
  OrderPageCreationBaseOptions & {
    alternativeCycle?: Cycle;
  }): Promise<OrderPageDataResponse> {
  const orderPageData = {
    productId: currentProduct.id,
    siteGuid,
    couponServiceFallback,
    paymentCycle: alternativeCycle
      ? convertCycleSelectorToPaymentCycle(alternativeCycle)
      : currentCycle,
    ...(couponCode && { couponCode }),
    ...(oldSiteGuid && { oldSiteGuid }),
    ...(existingUserPackageGuid && { existingUserPackageGuid }),
    ...(isDowngradePlanOfDeclineChargeService && { isDowngrade: true }),
  };
  return ApiManager.post(CheckoutUrls.CLASSIC_ORDER_PAGE_DATA, orderPageData);
}

export function useOrderPageCreation(
  options: UseOrderPageCreationOptions,
): OrderPageCreationFunction {
  const { dispatch } = useCheckoutStore();
  const {
    siteGuid,
    currentProduct,
    currentCycle,
    isDowngradePlanOfDeclineChargeService,
    dynamicOfferingRequestParams,
    classicRequestParams,
  } = options;
  if (dynamicOfferingRequestParams || classicRequestParams) {
    return async (alternativeCycle?: CycleSelector) => {
      const newCycle = alternativeCycle as Cycle;
      dispatch(CheckoutActions.setAlternativeCycle(newCycle));
      if (dynamicOfferingRequestParams) {
        return fetchDynamicOfferingOrderPageData({
          ...dynamicOfferingRequestParams,
          siteGuid,
          currentCycle,
          currentProduct,
          isDowngradePlanOfDeclineChargeService,
          alternativeCycle: newCycle,
        });
      } else if (classicRequestParams) {
        return fetchClassicOrderPageData({
          ...classicRequestParams,
          siteGuid,
          currentCycle,
          currentProduct,
          isDowngradePlanOfDeclineChargeService,
          alternativeCycle: newCycle,
        });
      }
      return Promise.reject(REJECT_REASON);
    };
  } else {
    throw new Error(REJECT_REASON);
  }
}
